import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Alert,
  Snackbar,
  Container,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useGetTableData2, usePutData } from "../Plan/tableHooks";
import { useParams } from "react-router-dom";
import * as M from "./styles";
import goToHelpLink from "../../../utils/goToHelpLink";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

function AdminSettings() {
  const { group_code, project_code } = useParams();
  const theme = useTheme();
  const [settings, setSettings] = useState({
    utilization_importance: "",
    optimization_strategy: "",
    bwd_only_allow_past: false,
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { data } = useGetTableData2(
    null,
    `/api/admin/data?group_code=${group_code}&project_code=${project_code}`
  );
  const updateAdminMutation = usePutData(null, "/api/admin/update");

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const loaded = data || {};
      setSettings({
        utilization_importance:
          loaded.utilization_importance !== undefined
            ? String(loaded.utilization_importance)
            : "",
        optimization_strategy:
          loaded.optimization_strategy !== undefined
            ? String(loaded.optimization_strategy)
            : "",
        bwd_only_allow_past:
          loaded.bwd_only_allow_past !== undefined
            ? loaded.bwd_only_allow_past === 1 ||
              loaded.bwd_only_allow_past === true
            : false,
      });
      setOriginalSettings({
        utilization_importance:
          loaded.utilization_importance !== undefined
            ? String(loaded.utilization_importance)
            : "",
        optimization_strategy:
          loaded.optimization_strategy !== undefined
            ? String(loaded.optimization_strategy)
            : "",
        bwd_only_allow_past:
          loaded.bwd_only_allow_past !== undefined
            ? loaded.bwd_only_allow_past === 1 ||
              loaded.bwd_only_allow_past === true
            : false,
      });
    } catch (error) {
      showSnackbar("설정 로드 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSelectChange = (event) => {
    setSettings((prev) => ({
      ...prev,
      optimization_strategy: event.target.value,
    }));
  };
  const handleCheckboxChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };
  const handleSave = async () => {
    if (!validateInputs()) return;
    setLoading(true);
    try {
      const obj = {
        ...settings,
        utilization_importance: Number(settings.utilization_importance),
        optimization_strategy: Number(settings.optimization_strategy),
        bwd_only_allow_past: settings.bwd_only_allow_past ? 1 : 0,
        group_code,
        project_code,
      };
      await updateAdminMutation.mutate(obj);
      setOriginalSettings({
        utilization_importance: String(obj.utilization_importance),
        optimization_strategy: String(obj.optimization_strategy),
        bwd_only_allow_past: obj.bwd_only_allow_past === 1,
      });
      setIsEditing(false);
      showSnackbar("설정이 성공적으로 저장되었습니다.", "success");
    } catch (error) {
      showSnackbar("저장 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    setSettings({ ...originalSettings });
    setIsEditing(false);
  };
  const validateInputs = () => {
    const { utilization_importance, optimization_strategy } = settings;
    if (
      utilization_importance === "" ||
      optimization_strategy === "" ||
      utilization_importance === undefined ||
      optimization_strategy === undefined
    ) {
      showSnackbar("필수 필드를 모두 입력/선택해주세요.", "warning");
      return false;
    }
    const utilNum = Number(utilization_importance);
    if (!Number.isInteger(utilNum) || utilNum < 1 || utilNum > 100) {
      showSnackbar(
        "장비 가동룔 중요도는 1~100 사이의 정수여야 합니다.",
        "warning"
      );
      return false;
    }
    const stratNum = Number(optimization_strategy);
    if (![1, 2, 3].includes(stratNum)) {
      showSnackbar("최적화 전략은 1, 2, 3 중 하나여야 합니다.", "warning");
      return false;
    }
    return true;
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const hasChanges =
    JSON.stringify(settings) !== JSON.stringify(originalSettings);
  const settingItems = [
    {
      key: "utilization_importance",
      label: "설비 가동룔 중요도",
      description: "장비의 base load에 최대한 맞추려는 노력의 정도",
      type: "number",
      min: 1,
      max: 100,
      icon: "⏰",
      extra: true,
    },
    {
      key: "optimization_strategy",
      label: "최적화 전략",
      description: "납기준수, 병목공정 최적화, 균형 전략 중 선택",
      type: "select",
      options: [
        { value: "1", label: "납기준수" },
        { value: "2", label: "병목공정 최적화" },
        { value: "3", label: "균형 전략" },
      ],
      icon: "🔄",
    },
    {
      key: "bwd_only_allow_past",
      label: "요구일 기준 역방향만 계획하기",
      description: "요구일 기준 역방향만 계획하도록 설정",
      type: "checkbox",
      icon: "🔄",
    },
  ];

  return (
    <>
      <div
        style={{
          background: "#ffffff",
          minHeight: "calc(100vh - 65px)",
          overflow: "hidden",
          paddingBottom: 48,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 2, md: 4 },
            //  width: "calc(100vw - 239px)",
            //   minWidth: "calc(100vw - 239px)",
          }}
        >
          <M.ToolbarContainer sx={{ py: 2 }}>
            <M.ToolbarLeft>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ fontSize: "22px !important", color: "#111" }}
              >
                AI 스케쥴링 기준 설정&nbsp;
                <Tooltip
                  title="이동"
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    goToHelpLink(
                      "http://esehelp.cafe24.com/planner#h-10-%EA%B8%B0%EC%A4%80%EC%A0%95%EB%B3%B4-%EC%8B%9C%EC%8A%A4%ED%85%9C-%EC%84%A4%EC%A0%95"
                    )
                  }
                >
                  <HelpOutlineIcon />
                </Tooltip>
              </Typography>
            </M.ToolbarLeft>
            <CardActions sx={{ padding: 0, justifyContent: "flex-end" }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                {!isEditing ? (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    설정 수정
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      취소
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={loading || !hasChanges}
                    >
                      {loading ? "저장 중..." : "저장하기"}
                    </Button>
                  </>
                )}
              </Box>
            </CardActions>
          </M.ToolbarContainer>
          <Paper
            sx={{
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              padding: { xs: 2, md: 3 },
              mt: 1,
              boxShadow: "none",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Alert
                icon={<InfoIcon />}
                severity="info"
                sx={{ borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ color: "#000" }}>
                  <strong>주의사항:</strong> 설정 변경 시 현재 실행 중인 생산
                  계획에 영향을 줄 수 있습니다. 변경 후에는 관련 시스템을
                  재시작하는 것을 권장합니다.
                </Typography>
              </Alert>
            </Box>
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {settingItems.map((item) => (
                  <Grid item xs={12} md={6} key={item.key}>
                    <Card
                      elevation={1}
                      sx={{
                        height: "100%",
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          transform: "translateY(-4px)",
                          transition: "all 0.2s ease",
                        },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        padding: "1rem",
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography sx={{ fontSize: 20, mr: 1 }}>
                              {item.icon}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {item.label}
                            </Typography>
                          </Box>

                          {item.key === "utilization_importance" && (
                            <Stack direction="row" spacing={0.5}>
                              <Chip
                                label="1~20: 낮음"
                                size="small"
                                sx={{
                                  bgcolor: "#E8F5E9",
                                  color: "#2E7D32",
                                  fontSize: 11,
                                }}
                              />
                              <Chip
                                label="21~50: 보통"
                                size="small"
                                sx={{
                                  bgcolor: "#FFF3E0",
                                  color: "#EF6C00",
                                  fontSize: 11,
                                }}
                              />
                              <Chip
                                label="51~80: 높음"
                                size="small"
                                sx={{
                                  bgcolor: "#FFE0B2",
                                  color: "#E65100",
                                  fontSize: 11,
                                }}
                              />
                              <Chip
                                label="81~100: 매우높음"
                                size="small"
                                sx={{
                                  bgcolor: "#FFEBEE",
                                  color: "#C62828",
                                  fontSize: 11,
                                }}
                              />
                            </Stack>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {item.description}
                        </Typography>

                        {item.type === "number" && (
                          <TextField
                            fullWidth
                            type="number"
                            value={settings[item.key] ?? ""}
                            onChange={handleInputChange(item.key)}
                            disabled={!isEditing || loading}
                            inputProps={{
                              min: item.min ?? 1,
                              max: item.max ?? 100,
                              step: 1,
                              style: {
                                fontSize: 16,
                                fontWeight: 600,
                                textAlign: "center",
                                padding: "10px 12px",
                              },
                            }}
                            sx={{
                              mt: 1,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1,
                                bgcolor: isEditing
                                  ? "background.paper"
                                  : "grey.50",
                                "&.Mui-disabled": {
                                  bgcolor: "grey.50",
                                },
                              },
                            }}
                          />
                        )}
                        {item.type === "select" && (
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id={`${item.key}-label`}>
                              선택
                            </InputLabel>
                            <Select
                              labelId={`${item.key}-label`}
                              value={settings[item.key] ?? ""}
                              label="선택"
                              onChange={handleSelectChange}
                              disabled={!isEditing || loading}
                              size="medium"
                              sx={{
                                "& .MuiSelect-select": {
                                  fontWeight: 600,
                                  textAlign: "center",
                                },
                              }}
                            >
                              {item.options.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {item.type === "checkbox" && (
                          <Box sx={{ mt: 1 }}>
                            {isEditing ? (
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={settings[item.key] ?? false}
                                    onChange={handleCheckboxChange(item.key)}
                                    disabled={loading}
                                    color="primary"
                                  />
                                }
                                label={settings[item.key] ? "TRUE" : "FALSE"}
                                sx={{
                                  "& .MuiFormControlLabel-label": {
                                    fontWeight: 600,
                                    fontSize: 16,
                                  },
                                }}
                              />
                            ) : (
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 16,
                                  textAlign: "center",
                                  py: 1,
                                  color: "text.primary",
                                }}
                              >
                                {settings[item.key] ? "TRUE" : "FALSE"}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
            <Divider />
          </Paper>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </div>
    </>
  );
}

export default AdminSettings;
